import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  ShieldCheck,
  FileCheck,
  Briefcase,
  ClipboardList,
  Users,
  Repeat,
  HeartPulse,
  Phone,
  MapPin,
  FileText,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/insurance-tpa';

const InsuranceTPA = () => {
  const seo = {
    title: 'Insurance & TPA Transportation Provider | Authorized NEMT | MoveraCare',
    description: 'Non-emergency medical transportation provider resource for insurance carriers and Third-Party Administrators (TPAs) administering authorized NEMT benefits across Greater Houston.',
    canonical: CANONICAL,
    schemaId: 'partner-insurance-tpa-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Insurance & TPA NEMT Provider Services',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'BusinessAudience',
        audienceType: 'Insurance Carriers, Third-Party Administrators (TPAs)',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-insurance-tpa"
      seo={seo}
      eyebrow="INSURANCE & TPAs"
      heading="A Transportation Provider Resource for Insurance Carriers &amp; TPAs"
      intro="MoveraCare Mobility is a Greater Houston non-emergency medical transportation provider prepared to work with insurance carriers, Third-Party Administrators, and benefit programs that authorize NEMT transportation for covered members."
      audience={{
        title: 'For Insurance & TPA Teams Administering NEMT Benefits',
        description: 'This page is for insurance and TPA teams responsible for evaluating, credentialing, or engaging non-emergency transportation providers as part of a covered benefit or claim workflow.',
        bullets: [
          'Insurance carrier network management teams',
          'Third-Party Administrators (TPAs) coordinating medical transportation',
          'Workers’ Compensation claim adjusters and coordinators',
          'Managed care organizations evaluating NEMT vendors',
          'Provider network credentialing teams',
          'Utilization and case management units',
        ],
      }}
      problem={{
        title: 'Reliable Transportation Capacity, Documented for Payers',
        description: 'Payers and administrators need transportation providers who can be relied on to complete authorized trips, document trip activity, and communicate clearly across the authorization-to-billing workflow.',
        bullets: [
          'Authorized non-emergency wheelchair and ambulatory transportation',
          'Documentation to support authorization, trip completion, and billing workflows',
          'Communication with adjusters, case managers, and utilization teams',
          'Reliable capacity for recurring authorized transportation schedules',
          'Credentialing and vendor documentation available upon request',
        ],
      }}
      capabilities={{
        title: 'Provider Capabilities Aligned to Payer Workflows',
        items: [
          { icon: Briefcase, title: 'Authorized Trip Fulfillment', desc: 'Non-emergency transportation of covered members to approved medical, therapy, and diagnostic destinations.' },
          { icon: FileCheck, title: 'Trip Documentation', desc: 'Trip records supporting authorization confirmation, completion, and downstream billing workflows.' },
          { icon: Repeat, title: 'Recurring Authorizations', desc: 'Capacity for standing authorizations — dialysis, therapy, rehabilitation, and other recurring benefits.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory', desc: 'Wheelchair-accessible vans and ambulatory transport; drivers assist passengers during transport.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Handling', desc: 'Professional handling of member information across dispatch and operations.' },
          { icon: ClipboardList, title: 'Adjuster & Coordinator Communication', desc: 'Point-of-contact workflows for adjusters, case managers, and utilization staff.' },
        ],
      }}
      coordination={{
        title: 'Authorization to Trip Completion',
        steps: [
          { title: 'Authorization Received', desc: 'Payer, TPA, or adjuster authorizes transportation for a covered member with trip parameters.' },
          { title: 'Trip Scheduled', desc: 'MoveraCare confirms trip details, vehicle type, pickup window, and any special assistance.' },
          { title: 'Transportation Completed', desc: 'Wheelchair or ambulatory transport completes the authorized trip with driver documentation.' },
          { title: 'Documentation Returned', desc: 'Trip completion documentation is available to support downstream billing and utilization workflows.' },
        ],
      }}
      why={{
        title: 'Why Insurance & TPA Teams Consider MoveraCare',
        items: [
          { icon: ShieldCheck, title: 'Credentialing-Ready Documentation', desc: 'Licensing, insurance, NPI, and vendor documentation prepared for payer credentialing review.' },
          { icon: FileText, title: 'Structured Trip Records', desc: 'Operational documentation supporting authorization, completion, and billing workflows.' },
          { icon: Repeat, title: 'Recurring Capacity', desc: 'Reliable capacity for recurring authorized transportation schedules across our service area.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport, supporting wheelchair and ambulatory transportation with dignity.' },
          { icon: Phone, title: 'Provider Relations Point of Contact', desc: 'Direct contact for credentialing, contracting, and payer relations questions.' },
          { icon: MapPin, title: 'Verified Service Area', desc: 'Greater Houston coverage across Harris, Fort Bend, Brazoria, Montgomery, and Galveston Counties.' },
        ],
      }}
      transportRequest={{
        title: 'Submit an Authorized Transportation Request',
        description: 'Adjusters, TPAs, and coordinators can submit non-emergency transportation requests directly to dispatch or contact provider relations for authorized trip coordination.',
        cta: { label: 'Request Patient Transportation', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Begin a Provider / Vendor Relationship',
        description: 'Credentialing, network management, and procurement teams can access MoveraCare’s vendor documentation packet or contact Provider Relations to begin the provider onboarding process.',
      }}
    />
  );
};

export default InsuranceTPA;
